import { useEffect, useRef } from 'react';

interface UsePollingOptions {
  enabled?: boolean;
  immediate?: boolean;
  preventOverlap?: boolean;
  pauseWhenHidden?: boolean;
}

export function usePolling(
  callback: () => void | Promise<void>,
  intervalMs: number | null,
  {
    enabled = true,
    immediate = false,
    preventOverlap = false,
    pauseWhenHidden = false,
  }: UsePollingOptions = {},
) {
  const callbackRef = useRef(callback);
  const inFlightRef = useRef(false);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!enabled || intervalMs === null) {
      inFlightRef.current = false;
      return;
    }

    let isActive = true;

    const runCallback = async () => {
      if (
        !isActive ||
        (pauseWhenHidden && typeof document !== 'undefined' && document.hidden)
      ) {
        return;
      }

      if (preventOverlap && inFlightRef.current) {
        return;
      }

      inFlightRef.current = true;

      try {
        await callbackRef.current();
      } finally {
        inFlightRef.current = false;
      }
    };

    if (immediate) {
      void runCallback();
    }

    const interval = window.setInterval(() => {
      void runCallback();
    }, intervalMs);

    return () => {
      isActive = false;
      inFlightRef.current = false;
      window.clearInterval(interval);
    };
  }, [enabled, immediate, intervalMs, pauseWhenHidden, preventOverlap]);
}
